// @ts-nocheck
// "My Dashboard" for a team lead — a supervisor board, not the agent
// dashboard. Live sections (roster, on-call, queue) come off the
// team-lead-real-time SSE feed; the lower sections are scoped by the same
// filter bar the agent dashboard uses. Every section is ACL-gated through
// features/cti2/acl.ts.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { RefreshCw } from 'lucide-react';
import { CallStatsBody } from '@/features/cti/components/AgentCallStatsPanel';
import FilterBarPill from '@/features/cti/components/call-stats/filters/FilterBarPill';
import { useCallStatsData } from '@/features/cti/components/call-stats/useCallStatsData';
import { dateToInputStr } from '@/features/cti/components/call-stats/format';
import LeadsScreen from '@/features/cti/screens/LeadsScreen';
import DashboardSection from '../components/DashboardSection';
import DashboardSectionNav from '../components/DashboardSectionNav';
import { useModuleOps } from '../hooks/useModuleOps';
import { ACL_DENIED_TEXT, isDenied, teamAccess } from '../acl';
import { useTeamRealtime } from '../team/useTeamRealtime';
import { useSnoop } from '../team/useSnoop';
import { queueWaiting } from '../team/teamModel';
import { EmptyNote, HAIRLINE, Panel } from '../team/teamUi';
import TeamLiveStatusBadge from '../team/TeamLiveStatusBadge';
import TeamKpiStrip from '../team/TeamKpiStrip';
import TeamAlertsBar from '../team/TeamAlertsBar';
import TeamGroupBoard from '../team/TeamGroupBoard';
import TeamOnCallPanel from '../team/TeamOnCallPanel';
import TeamLeadLiveBoard from '@/features/live-calls/components/TeamLeadLiveBoard';
import TeamQueuePanel from '../team/TeamQueuePanel';
import TeamPerformance from '../team/TeamPerformance';
import LoggedInAgentBreakTable from '@/features/cti/components/call-stats/LoggedInAgentBreakTable';
import AgentDetailDialog from '../team/AgentDetailDialog';
import { TeamCampaigns, TeamLedGroups, useTeamLeadList } from '../team/TeamAssignments';
import AgentGroupsSection from '../components/AgentGroupsSection';
import { API_TEAM_LEAD_CAMPAIGNS, API_TEAM_LEAD_GROUPS } from '@/service/url';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import CallLogTable from '@/features/cti/components/call-stats/CallLogTable';
import { useTeamCallLog } from '../team/useTeamCallLog';
import DispositionFollowUpsPanel from '@/features/dashboard/widgets/DispositionFollowUpsPanel';
import { canUseDispositionFollowUpsFeature } from '@/utilities/dispositionFormsAcl';

function RefreshButton({ loading, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Refresh"
      className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
    >
      <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
    </button>
  );
}

export default function TeamDashboardView({ userData, theme = 'light', isOutboundEnabled = false, onCallLead, onRequestDashboard }) {
  const scrollRootRef = useRef(null);
  const navElRef = useRef(null);
  const filterBarRef = useRef(null);
  const stuckHeightRef = useRef(56);

  const isDark = theme === 'dark';
  const agentId = userData?.agentData?.id;
  const moduleOps = useModuleOps();
  const acl = useMemo(() => teamAccess(moduleOps), [moduleOps]);

  const team = useTeamRealtime(acl.canReadAgents);
  const snoop = useSnoop(acl.canSnoop);
  const csData = useCallStatsData(agentId);

  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const showGroups = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'read');
  const showCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'read');
  const ledGroups = useTeamLeadList(API_TEAM_LEAD_GROUPS, showGroups);
  const teamCampaigns = useTeamLeadList(API_TEAM_LEAD_CAMPAIGNS, showCampaigns);
  // team-lead-calls sits behind Reports read, same as the agent's own call log.
  const canReadTeamCalls = !isDenied(moduleOps, 'Reports', 'read');
  const teamCalls = useTeamCallLog(csData.startDate, csData.endDate, canReadTeamCalls);
  // Read-only view of the mapped agents' follow-ups (server scopes by agent_team_lead_config).
  const canUseFollowUps = canUseDispositionFollowUpsFeature(moduleOps, { isSuperAdmin });

  // Monitor/Whisper/Barge's own start/stop already reset busy/session
  // cleanly on an API failure — the gap was the call itself dropping out
  // from under an active session (agent hangs up, goes offline) with no
  // API failure to catch. Left unwatched, the "Stop {mode}" button stayed
  // pressed indefinitely for a call that no longer exists.
  useEffect(() => {
    if (!snoop.session) return;
    const row = team.allAgents?.find((r) => r.agent?.id === snoop.session.agentId);
    if (!row || !row.snoop?.answered) snoop.reset();
  }, [team.allAgents, snoop.session, snoop]);

  const [stateFilter, setStateFilter] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState('team-live');
  const [leadsPending, setLeadsPending] = useState(0);
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('all');
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');
  const [leadsPortalNode, setLeadsPortalNode] = useState(null);
  const [teamLivePortalNode, setTeamLivePortalNode] = useState(null);
  const [followUpsPending, setFollowUpsPending] = useState(0);
  const [followUpsStatus, setFollowUpsStatus] = useState('');
  const [followUpsSearch, setFollowUpsSearch] = useState('');
  const [followUpsPortalNode, setFollowUpsPortalNode] = useState(null);
  const [followUpsRefreshToken, setFollowUpsRefreshToken] = useState(0);
  // This view now stays permanently mounted (see AgentPortalPage.tsx), so
  // this effect only ever runs once — if Topbar's own JSX around
  // #ap-topbar-tabs-slot ever changes enough to recreate that element
  // (conditional rendering, HMR during dev, etc.), a one-shot lookup would
  // go stale forever, silently portaling into a detached node. Re-checking
  // on a light interval instead self-heals: no visible cost when nothing
  // changed (isConnected stays true and the state setter is a no-op).
  const [topbarTabsNode, setTopbarTabsNode] = useState(null);
  useEffect(() => {
    const check = () => {
      setTopbarTabsNode((prev) => (
        prev && prev.isConnected ? prev : document.getElementById('ap-topbar-tabs-slot')
      ));
    };
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const root = scrollRootRef.current;
    const filterEl = filterBarRef.current;
    if (!root || !filterEl) return undefined;

    // Nav's own height no longer counts here — it moved out of this
    // scrollable area entirely (portaled into Topbar's persistent header).
    const GAP = 12;
    const applyOffset = () => {
      const offset = filterEl.offsetHeight + GAP;
      stuckHeightRef.current = offset;
      root.style.setProperty('--section-scroll-offset', `${offset}px`);
    };

    applyOffset();
    const ro = new ResizeObserver(applyOffset);
    ro.observe(filterEl);
    return () => ro.disconnect();
  }, []);

  const sections = useMemo(
    () => [
      { id: 'team-live', label: 'Team Live' },
      { id: 'live-calls', label: 'Live Calls' },
      { id: 'on-call', label: 'On Call' },
      { id: 'queue', label: 'Call Group', badge: queueWaiting(team.queue) },
      { id: 'team-performance', label: 'Performance' },
      canReadTeamCalls && { id: 'team-calls', label: 'Team Calls' },
      canUseFollowUps && { id: 'team-follow-ups', label: 'Follow-Ups', badge: followUpsPending },
      acl.canReadLeads && { id: 'team-leads', label: 'My Leads', badge: leadsPending },
      { id: 'my-stats', label: 'My Stats' },
    ].filter(Boolean),
    [team.queue, leadsPending, acl.canReadLeads, canReadTeamCalls, canUseFollowUps, followUpsPending],
  );

  const filterFromStr = useMemo(() => dateToInputStr(csData.startDate), [csData.startDate]);
  const filterToStr = useMemo(() => dateToInputStr(csData.endDate), [csData.endDate]);
  // Same as the agent dashboard: "Today" also shows the next 30 days of follow-ups.
  const followUpsToStr = useMemo(
    () => (csData.activePreset === 'today' ? moment().add(30, 'days').format('YYYY-MM-DD') : filterToStr),
    [csData.activePreset, filterToStr],
  );

  const rangeLabel = useMemo(() => {
    const from = moment(csData.startDate);
    const to = moment(csData.endDate);
    return from.isSame(to, 'day')
      ? from.format('D MMM YYYY')
      : `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
  }, [csData.startDate, csData.endDate]);

  const handlePresetChange = useCallback(
    (key) => {
      if (key) csData.applyPreset(key);
    },
    [csData.applyPreset],
  );

  const handleRangeChange = useCallback(
    (range) => {
      if (!range?.from) return;
      const end = range.to || range.from;
      csData.setStartDate(range.from);
      csData.setEndDate(end);
      csData.setActivePreset('');
      if (range.to) csData.loadAll(range.from, end);
    },
    [csData.setStartDate, csData.setEndDate, csData.setActivePreset, csData.loadAll],
  );

  const handleAlertPick = useCallback((alert) => {
    setStateFilter(alert.key === 'break' ? 'break' : null);
    document.getElementById('team-live')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const teamName = userData?.agentData?.agent_name || userData?.username || 'My team';

  return (
    <div
      ref={scrollRootRef}
      className="-mt-2 flex h-full min-h-0 flex-col overflow-y-auto rounded-b-2xl bg-card px-2 pb-2 shadow-neu-raised"
    >
      {topbarTabsNode && createPortal(
        <DashboardSectionNav
          items={sections}
          scrollRootRef={scrollRootRef}
          onActiveChange={setActiveSectionId}
          navRef={navElRef}
          stuckHeightRef={stuckHeightRef}
          onBeforeNavigate={onRequestDashboard}
        />,
        topbarTabsNode,
      )}

      <div
        ref={filterBarRef}
        className={`sticky top-0 z-[5] -mx-2 mb-3 border-b ${HAIRLINE} bg-card px-5 py-3 shadow-sm`}
      >
        <FilterBarPill
          activePreset={csData.activePreset}
          startDate={csData.startDate}
          endDate={csData.endDate}
          onPresetChange={handlePresetChange}
          onRangeChange={handleRangeChange}
          onReset={csData.handleReset}
          onRefresh={() => {
            csData.handleRefresh();
            team.refreshStats();
            if (canReadTeamCalls) teamCalls.refresh();
            setFollowUpsRefreshToken((n) => n + 1);
          }}
          rangeLabel={`${rangeLabel} · scopes Team Calls, Follow-Ups, My Leads and My Stats`}
          extra={
            <>
              {activeSectionId === 'team-live' && (
                <div ref={setTeamLivePortalNode} className="flex items-center gap-2" />
              )}
              {activeSectionId === 'team-leads' && (
                <div ref={setLeadsPortalNode} className="flex items-center gap-2" />
              )}
              {activeSectionId === 'team-follow-ups' && (
                <div ref={setFollowUpsPortalNode} className="flex items-center gap-2" />
              )}
            </>
          }
        />
      </div>

      <div className="isolate space-y-7 pb-[60vh]">
        <DashboardSection
          id="team-live"
          eyebrow="My team"
          title={teamName}
          subtitle="Live agent status across your call groups — updates as it happens, not affected by the date filter"
          framed={false}
          right={
            <div className="flex items-center gap-2">
              <TeamLiveStatusBadge connected={team.connected} />
              <button
                type="button"
                onClick={team.refreshStats}
                title="Refresh counters"
                disabled={!acl.canReadAgents || team.statsLoading}
                className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
              >
                <RefreshCw size={13} className={team.statsLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          }
        >
          {!acl.canReadAgents ? (
            <Panel className="p-4">
              <EmptyNote>{ACL_DENIED_TEXT.agents}</EmptyNote>
            </Panel>
          ) : (
            <div className="space-y-3">
              <TeamKpiStrip
                counts={team.counts}
                totals={team.totals}
                stateFilter={stateFilter}
                onStateFilter={setStateFilter}
                queueWaiting={queueWaiting(team.queue)}
              />
              <TeamAlertsBar alerts={team.alerts} onPick={handleAlertPick} />
              {!acl.canReadCallGroup ? (
                <Panel className="p-4">
                  <EmptyNote>{ACL_DENIED_TEXT.callGroup}</EmptyNote>
                </Panel>
              ) : team.loading ? (
                <Panel className="p-4">
                  <EmptyNote>Connecting to the live team feed…</EmptyNote>
                </Panel>
              ) : (
                <TeamGroupBoard
                  groups={team.groups}
                  totalAgents={team.counts.total}
                  stateFilter={stateFilter}
                  onStateFilter={setStateFilter}
                  snoop={snoop}
                  canSnoop={acl.canSnoop}
                  onOpenAgent={setDetailRow}
                  filterPortalTarget={activeSectionId === 'team-live' ? teamLivePortalNode : null}
                />
              )}
            </div>
          )}
        </DashboardSection>

        <DashboardSection
          id="live-calls"
          eyebrow="Live calls"
          title="Call progress"
          subtitle="Every call of your agents as it moves — ringing, queued, connected, on hold, transferring"
          framed={false}
        >
          {!acl.canReadAgents ? (
            <Panel className="p-4">
              <EmptyNote>{ACL_DENIED_TEXT.agents}</EmptyNote>
            </Panel>
          ) : (
            <TeamLeadLiveBoard snoop={snoop} canSnoop={acl.canSnoop} embedded />
          )}
        </DashboardSection>

        <DashboardSection
          id="on-call"
          eyebrow="Live calls"
          title="On call now"
          subtitle="Listen in, whisper to the agent, or barge into the call"
          framed={false}
        >
          {!acl.canReadAgents ? (
            <Panel className="p-4">
              <EmptyNote>{ACL_DENIED_TEXT.agents}</EmptyNote>
            </Panel>
          ) : (
            <TeamOnCallPanel
              agents={team.allAgents}
              snoop={snoop}
              canSnoop={acl.canSnoop}
              onOpenAgent={setDetailRow}
            />
          )}
        </DashboardSection>

        <DashboardSection
          id="queue"
          eyebrow="Waiting"
          title="Call Group"
          subtitle="Customers holding for one of your call groups"
          framed={false}
        >
          {!acl.canReadCallGroup ? (
            <Panel className="p-4">
              <EmptyNote>{ACL_DENIED_TEXT.callGroup}</EmptyNote>
            </Panel>
          ) : (
            <TeamQueuePanel queue={team.queue} now={team.now} />
          )}
        </DashboardSection>

        <DashboardSection
          id="team-performance"
          eyebrow="Analytics"
          title="Team performance"
          subtitle="Today's per-agent outcomes — not scoped by the date filter (see section for why)"
          framed={false}
        >
          {!acl.canReadAgents ? (
            <Panel className="p-4">
              <EmptyNote>{ACL_DENIED_TEXT.agents}</EmptyNote>
            </Panel>
          ) : (
            <div className="flex flex-col gap-3">
              <TeamPerformance agents={team.allAgents} totals={team.totals} isDark={isDark} />
              <LoggedInAgentBreakTable agents={team.allAgents} loading={team.loading} />
            </div>
          )}
        </DashboardSection>

        {canReadTeamCalls && (
          <DashboardSection
            id="team-calls"
            eyebrow="Activity"
            title="Team Call Log"
            subtitle="Calls of every agent under you, across your call groups — filtered by the date range above"
          >
            <CallLogTable
              rows={teamCalls.rows}
              loading={teamCalls.loading}
              showAgent
              tableId="cti.team-lead.team-calls.v1"
              exportName="team-calls"
              serverPaging={teamCalls.paging}
              onSearchChange={teamCalls.changeSearch}
              fetchExportRows={teamCalls.fetchExport}
              exportLabel="Export these calls"
            />
          </DashboardSection>
        )}

        {canUseFollowUps && (
          <DashboardSection
            id="team-follow-ups"
            eyebrow="Scheduled"
            title="Team Follow-Ups"
            subtitle="Callbacks your agents committed to on a disposition — view only"
          >
            <DispositionFollowUpsPanel
              readOnly
              refreshToken={followUpsRefreshToken}
              onPendingCountChange={setFollowUpsPending}
              fromDate={filterFromStr}
              toDate={followUpsToStr}
              status={followUpsStatus}
              onStatusChange={setFollowUpsStatus}
              search={followUpsSearch}
              onSearchChange={setFollowUpsSearch}
              filterPortalTarget={activeSectionId === 'team-follow-ups' ? followUpsPortalNode : null}
            />
          </DashboardSection>
        )}

        {acl.canReadLeads && (
          <DashboardSection id="team-leads" eyebrow="Pipeline" title="My Leads" subtitle="Leads assigned to you">
            <LeadsScreen
              active
              isOutboundEnabled={isOutboundEnabled}
              onCallLead={onCallLead}
              onClose={() => {}}
              onPendingCountChange={setLeadsPending}
              fromDate={filterFromStr}
              toDate={filterToStr}
              statusFilter={leadsStatusFilter}
              onStatusFilterChange={setLeadsStatusFilter}
              searchTerm={leadsSearchTerm}
              onSearchTermChange={setLeadsSearchTerm}
              filterPortalTarget={activeSectionId === 'team-leads' ? leadsPortalNode : null}
            />
          </DashboardSection>
        )}

        <DashboardSection
          id="my-stats"
          eyebrow="Analytics"
          title="My own calls"
          subtitle="Your personal call analytics for the selected date range"
          framed={false}
        >
          <CallStatsBody
            agentUsername={userData?.username || userData?.agentData?.agent_name}
            theme={theme}
            showHeading={false}
            showCallLog
            showQuickPeriods={false}
            showFilter={false}
            callLogExportLabel="Export these calls"
            data={csData}
          />
        </DashboardSection>

        {showGroups && (
          <DashboardSection
            id="groups"
            eyebrow="Assignment"
            title="My Groups"
            subtitle="Call groups you lead, with live agent status — plus groups you take calls in yourself"
            framed={false}
            right={<RefreshButton loading={ledGroups.loading} onClick={ledGroups.refresh} />}
          >
            <div className="space-y-4">
              <TeamLedGroups groups={ledGroups.rows} loading={ledGroups.loading} liveGroups={team.groups} />
              <Panel className="p-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Your own group memberships
                </div>
                <AgentGroupsSection agentId={agentId} userData={userData} />
              </Panel>
            </div>
          </DashboardSection>
        )}

        {showCampaigns && (
          <DashboardSection
            id="campaigns"
            eyebrow="Assignment"
            title="Team Campaigns"
            subtitle="Active campaigns your team's agents are on, and which agents"
            framed={false}
            right={<RefreshButton loading={teamCampaigns.loading} onClick={teamCampaigns.refresh} />}
          >
            <TeamCampaigns campaigns={teamCampaigns.rows} loading={teamCampaigns.loading} />
          </DashboardSection>
        )}
      </div>

      <AgentDetailDialog
        row={detailRow}
        onClose={() => setDetailRow(null)}
        snoop={snoop}
        canSnoop={acl.canSnoop}
      />
    </div>
  );
}
